// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// A collapsible bottom attribute-table for the loaded geodatabase. It shows one of three sources, in priority:
//   1. QUERY RESULT — the rows a run_sql answer returned (even a prose answer with no map display); client-side
//      paginated (rows are already in hand). "browse layers" dismisses it.
//   2. HIGHLIGHT — the features Khora just displayed on the map (a filtered subset); a "show all" chip returns
//      to the full layer.
//   3. LAYER BROWSE — the DEFAULT: a picker browses any gdb layer from load, no Khora question needed.
// Highlight/browse page LAZILY (count from the keys/ObjectIDs, one <=50-row query per page — never a bulk fetch
// that hangs); a row with geometry flies the camera to it on click. Collapsed to a lone bottom-center arrow.

import React, { useEffect, useState } from 'react';
import {
  Box, CircularProgress, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ChatMapHighlight } from 'Features/dashboard/dashboardSlice';
import { ChatQueryResult } from 'Features/chatbot/chatbotTypes';
import { buildLayerMap } from 'Features/dashboard/dashboardMetrics';
import {
  prepareHighlightRows, prepareLayerRows, fetchHighlightRowsPage, gdbLayerOptions, HighlightRow, HighlightPlan,
} from './chatMapHighlight';

type ViewArg = Parameters<typeof fetchHighlightRowsPage>[0];
type LayerOption = { name: string; title: string };

interface Props {
  view: ViewArg;
  highlight: ChatMapHighlight | null;
  queryResult?: ChatQueryResult | null;
  getLayers: () => Parameters<typeof buildLayerMap>[0];
  gdbReadyKey?: string | null; // changes when a gdb loads → (re)build the layer picker
}

const PAGE_SIZE = 50;
const EMPTY_PLAN: HighlightPlan = { descriptors: [], total: 0, title: '' };

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
  return String(value);
};

const unionKeys = (rows: Record<string, unknown>[]): string[] => {
  const keys: string[] = [];
  rows.forEach((row) => Object.keys(row).forEach((key) => { if (!keys.includes(key)) keys.push(key); }));
  return keys;
};

const pickDefaultLayer = (options: LayerOption[]): string =>
  (options.find((option) => /pole|up_bottom/i.test(option.name)) ?? options[0])?.name ?? '';

const GdbRowsPanel = ({ view, highlight, queryResult, getLayers, gdbReadyKey }: Props): JSX.Element | null => {
  const [open, setOpen] = useState(false);
  const [layerOptions, setLayerOptions] = useState<LayerOption[]>([]);
  const [selectedLayer, setSelectedLayer] = useState('');
  const [filterHighlight, setFilterHighlight] = useState<ChatMapHighlight | null>(null);
  // A run_sql result the user chose to dismiss (they clicked "browse layers"); identity-compared, so a NEW
  // query result (a later turn) shows again.
  const [dismissedQuery, setDismissedQuery] = useState<ChatQueryResult | null>(null);
  const [plan, setPlan] = useState<HighlightPlan>(EMPTY_PLAN);
  const [page, setPage] = useState(0);
  const [columns, setColumns] = useState<string[]>([]);
  const [pageRows, setPageRows] = useState<HighlightRow[]>([]);
  const [loading, setLoading] = useState(false);

  const queryActive = Boolean(queryResult && queryResult.rows.length) && queryResult !== dismissedQuery;

  // Build the layer picker when a gdb loads (or the panel first opens, in case layers landed after mount).
  useEffect(() => {
    const options = gdbLayerOptions(getLayers());
    setLayerOptions(options);
    setSelectedLayer((prev) => (prev && options.some((o) => o.name === prev) ? prev : pickDefaultLayer(options)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gdbReadyKey, open]);

  // A NEW Khora highlight with rows filters the table to it (and jumps the picker to its layer); when it clears,
  // drop back to browsing the selected layer.
  useEffect(() => {
    const hasRows = highlight ? prepareHighlightRows(highlight).total > 0 : false;
    setFilterHighlight(hasRows ? highlight : null);
    const targetLayer = highlight?.targets?.find((target) => target.layer)?.layer;
    if (hasRows && targetLayer) setSelectedLayer(targetLayer);
  }, [highlight]);

  // Reset the page + columns whenever the SOURCE changes (a new query result / highlight / browsed layer,
  // OR the user dismisses the query via "browse layers", which flips queryActive without changing the others).
  useEffect(() => {
    setPage(0);
    setColumns([]);
    setPageRows([]);
  }, [queryResult, filterHighlight, selectedLayer, queryActive]);

  // Compute the descriptor PLAN for the HIGHLIGHT / BROWSE sources (skipped while a query result is showing).
  useEffect(() => {
    let cancelled = false;
    if (queryActive) return undefined; // query rows are materialized — no descriptor plan needed
    if (filterHighlight) { setPlan(prepareHighlightRows(filterHighlight)); return undefined; }
    if (!view || !selectedLayer) { setPlan(EMPTY_PLAN); return undefined; }
    setLoading(true);
    prepareLayerRows(view, getLayers(), selectedLayer).then((next) => {
      if (cancelled) return;
      setPlan(next);
      setLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryActive, filterHighlight, selectedLayer, view]);

  // Materialize the current page's rows while open: a query result slices client-side (no fetch), a highlight /
  // browsed layer queries only this page's <=50 keys.
  useEffect(() => {
    let cancelled = false;
    if (!open) return undefined;
    if (queryActive && queryResult) {
      const from = page * PAGE_SIZE;
      setColumns(queryResult.columns?.length ? queryResult.columns : unionKeys(queryResult.rows));
      setPageRows(queryResult.rows.slice(from, from + PAGE_SIZE).map((values, index) => (
        { key: `q:${from + index}`, values, geometry: null, layerTitle: '' }
      )));
      setLoading(false);
      return undefined;
    }
    if (!view || !plan.total) return undefined;
    const slice = plan.descriptors.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    setLoading(true);
    fetchHighlightRowsPage(view, getLayers(), slice).then((result) => {
      if (cancelled) return;
      setPageRows(result.rows);
      setColumns((prev) => (prev.length ? prev : result.columns));
      setLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, plan, view, queryActive, queryResult]);

  const total = queryActive && queryResult ? queryResult.rows.length : plan.total;
  const available = queryActive || Boolean(gdbReadyKey) || layerOptions.length > 0 || plan.total > 0;
  if (!available) return null; // no gdb loaded and nothing displayed → render nothing.

  const locate = (row: HighlightRow): void => {
    if (!view || !row.geometry) return; // query rows carry no geometry — nothing to fly to
    view.goTo({ target: row.geometry }, { duration: 700 }).catch(() => { /* interrupted */ });
  };

  if (!open) {
    const label = queryActive
      ? `Show ${total} query row${total === 1 ? '' : 's'}`
      : (filterHighlight ? `Show ${total} highlighted row${total === 1 ? '' : 's'}` : 'Show the geodatabase rows');
    return (
      <MuiTooltip title={label}>
        <IconButton aria-label="Show geodatabase rows" onClick={() => setOpen(true)} sx={styles.arrowButton}>
          <KeyboardArrowUpIcon />
        </IconButton>
      </MuiTooltip>
    );
  }

  return (
    <Paper elevation={6} sx={styles.panel}>
      <Box sx={styles.header}>
        <TableChartOutlinedIcon fontSize="small" />
        {queryActive ? (
          <>
            <MuiTooltip title={queryResult?.sql || "Khora's query result"}>
              <Typography variant="subtitle2" noWrap sx={{ maxWidth: 240 }}>Query result</Typography>
            </MuiTooltip>
            <Box component="button" type="button" onClick={() => setDismissedQuery(queryResult ?? null)} sx={styles.chip}>
              browse layers
            </Box>
          </>
        ) : layerOptions.length ? (
          <Select
            size="small"
            variant="standard"
            value={selectedLayer}
            onChange={(event) => { setFilterHighlight(null); setSelectedLayer(event.target.value); }}
            sx={styles.layerSelect}
          >
            {layerOptions.map((option) => (
              <MenuItem key={option.name} value={option.name} sx={{ fontSize: '0.8rem' }}>{option.title}</MenuItem>
            ))}
          </Select>
        ) : (
          <Typography variant="subtitle2">Displayed features</Typography>
        )}
        <Typography variant="caption" noWrap sx={{ color: 'text.secondary', flexShrink: 0 }}>
          {total} row{total === 1 ? '' : 's'}
        </Typography>
        {filterHighlight && !queryActive && (
          <MuiTooltip title="Showing only Khora's highlighted features — click to browse the whole layer">
            <Box component="button" type="button" onClick={() => setFilterHighlight(null)} sx={styles.chip}>
              filtered · show all
            </Box>
          </MuiTooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={14} />}
        <MuiTooltip title="Hide table">
          <IconButton size="small" aria-label="Hide table" onClick={() => setOpen(false)}>
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
      </Box>
      <TableContainer sx={styles.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (<TableCell key={column} sx={styles.th}>{column}</TableCell>))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => (
              <TableRow key={row.key} hover onClick={() => locate(row)} sx={row.geometry ? styles.rowClickable : undefined}>
                {columns.map((column) => (
                  <TableCell key={column} sx={styles.td} title={formatCell(row.values[column])}>
                    {formatCell(row.values[column])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_event, next) => setPage(next)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[]}
        sx={styles.pagination}
      />
    </Paper>
  );
};

const styles = {
  arrowButton: {
    position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 5,
    bgcolor: 'background.paper', color: 'text.primary', boxShadow: 3,
    '&:hover': { bgcolor: 'background.paper' },
  },
  panel: {
    position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 5,
    width: 'min(72vw, 960px)', maxWidth: 'calc(100% - 24px)', maxHeight: '44vh',
    display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, flexShrink: 0,
    borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
  },
  layerSelect: { fontSize: '0.8rem', fontWeight: 600, minWidth: 120, maxWidth: 240 },
  chip: {
    cursor: 'pointer', border: '1px solid', borderColor: 'primary.main', borderRadius: 999,
    px: 1, py: 0.25, fontSize: '0.66rem', fontFamily: 'monospace', color: 'primary.main',
    background: 'transparent', whiteSpace: 'nowrap',
  },
  tableContainer: { flex: '1 1 auto', minHeight: 0, overflow: 'auto' },
  th: { fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.paper', fontSize: '0.72rem' },
  td: {
    whiteSpace: 'nowrap', fontSize: '0.72rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
  },
  rowClickable: { cursor: 'pointer' },
  pagination: { borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 },
} as const;

export default GdbRowsPanel;
